import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Paper, Button, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Divider,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { useNotification } from "../../providers/NotificationProvider";
import { fetchProject } from "../../api/memberApi";

const formatDate = (iso) => {
  if (!iso) return "—";
  return iso.substring(0, 10).replace(/-/g, "/");
};

export default function ProjectGoalListPage() {
  const { businessDomainId, businessUnitId, projectId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });
  const basePath = `/strategy/${businessDomainId}/business-units/${businessUnitId}/projects/${projectId}`;
  const [goals, setGoals] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const d = await fetchProject(projectId);
      setGoals((d.goals || []).map((g, i) => ({ id: i, ...g })));
      setAchievements((d.achievements || []).map((a, i) => ({ id: i, ...a })));
    } catch (e) {
      notify.apiError(e);
    } finally {
      setLoading(false);
    }
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); }, [loadData]);

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(basePath)} variant="text">
          {t("common.back")}
        </Button>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>{t("project.goal.list.title")}</Typography>
      </Box>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}><CircularProgress /></Box>
      ) : (
        <>
          {goals.length === 0 ? (
            <Typography color="text.secondary">{t("common.noData")}</Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "grey.50" }}>
                    <TableCell sx={{ fontWeight: 600 }}>{t("project.goal.indicator")}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{t("project.goal.plannedValue")}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{t("project.goal.unit")}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{t("project.goal.plannedDate")}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{t("project.goal.deadline")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {goals.map((row) => (
                    <TableRow
                      key={row.id}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => navigate(`${basePath}/goals/${row.id}`)}
                    >
                      <TableCell>{row.indicator?.indicatorName || "—"}</TableCell>
                      <TableCell>{row.plannedValue ?? "—"}</TableCell>
                      <TableCell>{row.indicator?.unit || "—"}</TableCell>
                      <TableCell>{formatDate(row.plannedDate)}</TableCell>
                      <TableCell>{formatDate(row.deadline)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {achievements.length > 0 && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>{t("project.achievement.list.title")}</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "grey.50" }}>
                      <TableCell sx={{ fontWeight: 600 }}>{t("project.goal.indicator")}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t("project.achievement.plannedValue")}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t("project.achievement.actualValue")}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t("project.goal.unit")}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t("project.achievement.actualDate")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {achievements.map((a) => (
                      <TableRow key={a.id}>
                        <TableCell>{a.goal?.indicator?.indicatorName || "—"}</TableCell>
                        <TableCell>{a.goal?.plannedValue ?? "—"}</TableCell>
                        <TableCell>{a.actualValue ?? "—"}</TableCell>
                        <TableCell>{a.goal?.indicator?.unit || "—"}</TableCell>
                        <TableCell>{formatDate(a.actualDate)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </>
      )}
    </Box>
  );
}
